import { useState } from 'react';
import { Icon } from '@iconify/react';
import { BeatPlayer, Bloom, useElementReveal, type BeatDef, type BeatStep } from '../beatKit';
import { useFlowState } from '../flowStateCtx';
import { FONT, PRIMARY, INK, SUBTLE, BORDER, SPACE } from './_beatStyle';

// One path choice. The whole card lights up when picked (blue border + tint + a
// blue icon tile + a check), so the choice is never ambiguous. Icon sits in a
// real tinted tile instead of floating.
function ChoiceCard({
  icon,
  title,
  sub,
  selected,
  onSelect,
}: {
  icon: string;
  title: string;
  sub: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACE.lg,
        width: '100%',
        textAlign: 'left',
        padding: `${SPACE.lg}px 18px`,
        borderRadius: 20,
        cursor: 'pointer',
        background: '#fff',
        border: selected ? `2px solid ${PRIMARY}` : `2px solid rgba(15,23,42,0.06)`,
        boxShadow: selected
          ? '0 10px 26px -12px rgba(19,91,235,0.40)'
          : '0 4px 16px -8px rgba(15,23,42,0.10)',
        transition: 'background 160ms ease-out, border-color 160ms ease-out, box-shadow 160ms ease-out',
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 16,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Both tiles read the same: a white chip with the blue icon, whether or
          // not the card is selected. Selection is shown by the card border + tint.
          background: '#fff',
          border: '1px solid rgba(15,23,42,0.08)',
          boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
        }}
      >
        <Icon icon={icon} width={26} height={26} style={{ color: PRIMARY }} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: FONT, fontSize: 17, fontWeight: 700, color: INK, lineHeight: 1.2 }}>
          {title}
        </div>
        <div
          style={{
            fontFamily: FONT,
            fontSize: 13.5,
            fontWeight: 500,
            color: SUBTLE,
            marginTop: SPACE.xs,
            lineHeight: 1.4,
          }}
        >
          {sub}
        </div>
      </div>
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: selected ? 'none' : `2px solid ${BORDER}`,
          background: selected ? PRIMARY : 'transparent',
        }}
      >
        {selected && <Icon icon="mdi:check" width={15} height={15} style={{ color: '#fff' }} />}
      </div>
    </button>
  );
}

function PathSelectionBeat(props?: Record<string, string>) {
  // No default pick: the cards reveal one by one and the user chooses. In Play
  // the choice writes to shared flow state; on the canvas it stays local.
  const flow = useFlowState();
  const [localSel, setLocalSel] = useState<'new' | 'exp' | null>(null);
  const sel = flow ? flow.path : localSel;
  const setSel = (v: 'new' | 'exp') => (flow ? flow.setPath(v) : setLocalSel(v));
  const reveal = useElementReveal(2);

  // Each card is its own step, so the coach line lands first, then "I already
  // track habits" fades in, then "I'm new to this" fades in after it.
  const steps: BeatStep[] = [
    {
      id: 'ask',
      speaker: 'coach',
      say: props?.coachLine ?? "For the next part of the process, I'd like to know:",
    },
    {
      id: 'exp',
      speaker: 'coach',
      render: (
        <Bloom show={reveal > 0}>
          <ChoiceCard
            icon="mdi:format-list-checks"
            title={props?.expTitle ?? 'I already track habits'}
            sub={props?.expSub ?? "Read me your list and we'll get it organized."}
            selected={sel === 'exp'}
            onSelect={() => setSel('exp')}
          />
        </Bloom>
      ),
    },
    {
      id: 'new',
      speaker: 'coach',
      render: (
        <Bloom show={reveal > 1}>
          <ChoiceCard
            icon="mdi:sprout-outline"
            title={props?.newTitle ?? "I'm new to this"}
            sub={props?.newSub ?? "We'll start fresh and build your first habits together."}
            selected={sel === 'new'}
            onSelect={() => setSel('new')}
          />
        </Bloom>
      ),
    },
  ];

  return <BeatPlayer steps={steps} />;
}

const pathSelectionBeat: BeatDef = {
  type: 'path-selection',
  group: 'Onboarding',
  label: 'Path choice',
  Comp: PathSelectionBeat,
};

export default pathSelectionBeat;
